(ns story-builder.define
  (:require [clojure.string :as str]
            [goog.labs.userAgent.device :as gdevice]
            [goog.labs.userAgent.platform :as gplatform]
            [goog.labs.userAgent.util :as ua-util]
            [story-builder.config :refer [load-config]]
            [story-builder.util.obfuscate :refer [obfuscate-str]]))

;; Those are compile-time constants.
;; Overriding order:
;; - default values in the source code,
;; - overridden by shadow-cljs.edn,
;; - overridden by shadow-cljs's command-line params.
;; See https://shadow-cljs.github.io/docs/UsersGuide.html#config-merge
;;     "Overriding the :closure-defines from the CLI"
(goog-define ^string  BUILD-ID     "")    ;; "studio", "sdk" or "story-server"
(goog-define ^string  BUILD-TARGET "")    ;; "browser" or "node-script"
(goog-define ^string  ENV-NAME     "dev") ;; "dev", "test", "beta" or "prod"
(goog-define ^boolean CYPRESS?     false) ;;

;; Some alias
(def studio?       ^boolean (= BUILD-ID "studio"))
(def sdk?          ^boolean (= BUILD-ID "sdk"))
(def story-server? ^boolean (= BUILD-ID "story-server"))
(def frontend?     ^boolean (= BUILD-TARGET "browser"))
(def env-dev?      ^boolean (= ENV-NAME "dev"))
(def env-beta?     ^boolean (= ENV-NAME "beta"))
(def env-test?     ^boolean (= ENV-NAME "test"))

(def sentry? (not env-dev?))

(def debug? ^boolean goog.DEBUG) ;; Should only relate to compilation settings (e.g. optimization).
(when debug? (enable-console-print!))

(def config (load-config ENV-NAME))

#_(println
   {:build-id     BUILD-ID
    :build-target BUILD-TARGET
    :env-name     ENV-NAME
    :debug?       debug?
    :config       config})

;; Debug config
(def loader?                   (get-in config [:debug :loader?]))
(def token-refresh?            (get-in config [:debug :token-refresh?]))
(def edit-lock?                (get-in config [:debug :edit-lock?]))
(def edit-listen?              (get-in config [:debug :edit-listen?]))
(def autosave?                 (get-in config [:debug :autosave?]))
(def log-reframe-events?       (get-in config [:debug :log-reframe-events?]))
(def log-global-handler-stack? (get-in config [:debug :log-global-handler-stack?]))

;; Do we set monitoring points for Cypress?
(def cypress? (or CYPRESS? (:cypress? config)))

(defn mobile? []
  (and frontend?
       (str/includes? js/window.navigator.userAgent "Mobile")))

(def tablet? gdevice/isTablet)

(def ios? (gplatform/isIos))

(def mac?
  (and frontend?
       (re-find #"(?i)mac" (str/lower-case (.-platform js/navigator)))))

(def fb?
  (or (ua-util/matchUserAgent "FBMD")
      (ua-util/matchUserAgent "FBAN")
      (ua-util/matchUserAgent "FBAV")))

(def instagram?
  (ua-util/matchUserAgent "Instagram"))

(def URL (-> config :endpoints :backend))

;; WONDERING: should those go into the config file or would it be overkill?
(def URLs
  (merge
   {:root                 URL
    :echo                 (if env-dev? (str URL "/tags/echo") "https://echo.nws.press")
    :cdn                  "https://cdn.thenewsroom.io/platform/"
    :cdnapi               URL ;; Can remove this in favour of root.
    :graphql              (str URL "/graphql/")
    :stories              (if env-dev? (str URL "/stories") "https://stories.nws.ai")
    :svr                  (obfuscate-str "https://svr.nws.ai")
    :transform            "https://transform.nws.ai"
    :transform-bunny      "https://transform-bv1.nws.ai"
    :transform-cloudflare "https://transform-cf1.nws.ai"
    :resources            (get-in config [:endpoints :resources])
    :resources-cloudflare (get-in config [:endpoints :resources-cloudflare])
    :resources-bunny      (get-in config [:endpoints :resources-bunny])}
   (when story-server?
     {:cache (get-in config [:cache :host])})))

(def uuid-namespace "7e3da8e6-fc7c-11ee-b9fc-9960b5de4415")

(def small-file-upload-limit 1000000)

(def upload-limit 120428800) ; TODO: re-adjust me when finished w/ upload.

(def refresh-token-interval (* 1000 60 5))

(def alert-expire 3000)

(def all-story-elements #{:share :grid :cta :branding :progress :sound :nav})

(def image-credits 1)
(def video-credits 3)

(def accepted-media-formats
  #{"image/webp"
    "image/jpeg"
    "image/png"
    "image/svg+xml"
    "image/gif"
    "video/mp4"
    "video/webm"
    "video/quicktime"})

(def accepted-media-extensions
  [".webp"
   ".jpg"
   ".jpeg"
   ".png"
   ".svg"
   ".mp4"
   ".webm"
   ".mov"])

(def iso-languages ; Source: https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
  [["ab" "Abkhazian"]
   ["aa" "Afar"]
   ["af" "Afrikaans"]
   ["ak" "Akan"]
   ["sq" "Albanian"]
   ["am" "Amharic"]
   ["ar" "Arabic"]
   ["an" "Aragonese"]
   ["hy" "Armenian"]
   ["as" "Assamese"]
   ["av" "Avaric"]
   ["ae" "Avestan"]
   ["ay" "Aymara"]
   ["az" "Azerbaijani"]
   ["bm" "Bambara"]
   ["ba" "Bashkir"]
   ["eu" "Basque"]
   ["be" "Belarusian"]
   ["bn" "Bengali"]
   ["bh" "Bihari"]
   ["bi" "Bislama"]
   ["bs" "Bosnian"]
   ["br" "Breton"]
   ["bg" "Bulgarian"]
   ["my" "Burmese"]
   ["ca" "Catalan"]
   ["ch" "Chamorro"]
   ["ce" "Chechen"]
   ["ny" "Chichewa"]
   ["zh" "Chinese"]
   ["cv" "Chuvash"]
   ["kw" "Cornish"]
   ["co" "Corsican"]
   ["cr" "Cree"]
   ["hr" "Croatian"]
   ["cs" "Czech"]
   ["da" "Danish"]
   ["dv" "Divehi"]
   ["nl" "Dutch"]
   ["dz" "Dzongkha"]
   ["en" "English"]
   ["eo" "Esperanto"]
   ["et" "Estonian"]
   ["ee" "Ewe"]
   ["fo" "Faroese"]
   ["fj" "Fijian"]
   ["fi" "Finnish"]
   ["fr" "French"]
   ["ff" "Fula"]
   ["gl" "Galician"]
   ["gd" "Gaelic"]
   ["ka" "Georgian"]
   ["de" "German"]
   ["el" "Greek"]
   ["gn" "Guarani"]
   ["gu" "Gujarati"]
   ["ht" "Haitian Creole"]
   ["ha" "Hausa"]
   ["he" "Hebrew"]
   ["hz" "Herero"]
   ["hi" "Hindi"]
   ["ho" "Hiri Motu"]
   ["hu" "Hungarian"]
   ["is" "Icelandic"]
   ["io" "Ido"]
   ["ig" "Igbo"]
   ["id" "Indonesian"]
   ["ia" "Interlingua"]
   ["ie" "Interlingue"]
   ["iu" "Inuktitut"]
   ["ik" "Inupiak"]
   ["ga" "Irish"]
   ["it" "Italian"]
   ["ja" "Japanese"]
   ["jv" "Javanese"]
   ["kl" "Kalaallisut"]
   ["kn" "Kannada"]
   ["kr" "Kanuri"]
   ["ks" "Kashmiri"]
   ["kk" "Kazakh"]
   ["km" "Khmer"]
   ["ki" "Kikuyu"]
   ["rw" "Kinyarwanda"]
   ["rn" "Kirundi"]
   ["ky" "Kyrgyz"]
   ["kv" "Komi"]
   ["kg" "Kongo"]
   ["ko" "Korean"]
   ["ku" "Kurdish"]
   ["kj" "Kwanyama"]
   ["lo" "Lao"]
   ["la" "Latin"]
   ["lv" "Latvian"]
   ["li" "Limburgish"]
   ["ln" "Lingala"]
   ["lt" "Lithuanian"]
   ["lu" "Luga-Katanga"]
   ["lg" "Luganda"]
   ["lb" "Luxembourgish"]
   ["gv" "Manx"]
   ["mk" "Macedonian"]
   ["mg" "Malagasy"]
   ["ms" "Malay"]
   ["ml" "Malayalam"]
   ["mt" "Maltese"]
   ["mi" "Maori"]
   ["mr" "Marathi"]
   ["mh" "Marshallese"]
   ["mo" "Moldavian"]
   ["mn" "Mongolian"]
   ["na" "Nauru"]
   ["nv" "Navajo"]
   ["ng" "Ndonga"]
   ["nd" "Northern Ndebele"]
   ["ne" "Nepali"]
   ["no" "Norwegian"]
   ["nb" "Norwegian bokmål"]
   ["nn" "Norwegian nynorsk"]
   ["ii" "Nuosu"]
   ["oc" "Occitan"]
   ["oj" "Ojibwe"]
   ["or" "Oriya"]
   ["om" "Oromo"]
   ["os" "Ossetian"]
   ["pi" "Pāli"]
   ["ps" "Pashto"]
   ["fa" "Persian"]
   ["pl" "Polish"]
   ["pt" "Portuguese"]
   ["pa" "Punjabi"]
   ["qu" "Quechua"]
   ["rm" "Romansh"]
   ["ro" "Romanian"]
   ["ru" "Russian"]
   ["se" "Sami"]
   ["sm" "Samoan"]
   ["sg" "Sango"]
   ["sa" "Sanskrit"]
   ["sr" "Serbian"]
   ["sh" "Serbo-Croatian"]
   ["st" "Sesotho"]
   ["tn" "Setswana"]
   ["sn" "Shona"]
   ["sd" "Sindhi"]
   ["si" "Sinhalese"]
   ["sk" "Slovak"]
   ["sl" "Slovenian"]
   ["so" "Somali"]
   ["nr" "Southern Ndebele"]
   ["es" "Spanish"]
   ["su" "Sundanese"]
   ["sw" "Swahili"]
   ["ss" "Swati"]
   ["sv" "Swedish"]
   ["tl" "Tagalog"]
   ["ty" "Tahitian"]
   ["tg" "Tajik"]
   ["ta" "Tamil"]
   ["tt" "Tatar"]
   ["te" "Telugu"]
   ["th" "Thai"]
   ["bo" "Tibetan"]
   ["ti" "Tigrinya"]
   ["to" "Tonga"]
   ["ts" "Tsonga"]
   ["tr" "Turkish"]
   ["tk" "Turkmen"]
   ["tw" "Twi"]
   ["ug" "Uyghur"]
   ["uk" "Ukrainian"]
   ["ur" "Urdu"]
   ["uz" "Uzbek"]
   ["ve" "Venda"]
   ["vi" "Vietnamese"]
   ["vo" "Volapük"]
   ["wa" "Wallon"]
   ["cy" "Welsh"]
   ["wo" "Wolof"]
   ["fy" "Western Frisian"]
   ["xh" "Xhosa"]
   ["yi" "Yiddish"]
   ["yo" "Yoruba"]
   ["za" "Zhuang"]
   ["zu" "Zulu"]])

(def social-auth-allowed-hosts
  #{"localhost"
    "test-studio.nws.ai"
    "beta-studio.nws.ai"
    "studio.nws.ai"
    "studio.roundtwo.co"
    "studio.airtype.co"})

(def social-auth?
  (and frontend?
       (contains? social-auth-allowed-hosts js/window.location.hostname)))

(def registration-allowed-hosts
  #{"localhost"
    "studio.nws.ai"
    "beta-studio.nws.ai"
    "studio.roundtwo.co"
    "studio.airtype.co"})

(def registration?
  (and frontend?
       (contains? registration-allowed-hosts js/window.location.hostname)))

(def copy-header "nws-ai-copy-data:")

(def main-media-id "main-media-panel")
(def result-media-id "result-category-media-panel")
(def new-result-media-id "new-result-category-media-panel")
(def interactives-media-id :interactives-media-component)

(def all-media-component-ids
  #{main-media-id
    result-media-id
    new-result-media-id
    interactives-media-id})
